import styled from 'styled-components';
import { ModalLayout, ModalTemplate } from '@ardoq/modal';
import { ErrorNotification } from '@ardoq/status-ui';
import { s32 } from '@ardoq/design-tokens';
import { ConnectionsAsyncOperationsState } from 'integrations/common/streams/connections/types';
import { Connection } from '@ardoq/api-types/integrations';
import { Stack } from '@ardoq/layout';
import { t, Trans, type I18nLocale } from '@ardoq/i18n';

type DeleteConnectionDialogParams = {
  connection: Connection;
  statuses: ConnectionsAsyncOperationsState;
  onCancel: () => void;
  onSubmit: () => void;
  configNames: string[];
  scheduleNames: string[];
  i18nLocale: I18nLocale;
};

export function DeleteConnectionDialog({
  statuses,
  connection,
  configNames,
  scheduleNames,
  onSubmit,
  onCancel,
  i18nLocale,
}: DeleteConnectionDialogParams) {
  return (
    <ModalTemplate
      headerText={t(
        i18nLocale,
        'integrations:deleteConnectionDialog.headerText',
        'Delete the connection permanently?'
      )}
      secondaryButtonText={t(
        i18nLocale,
        'integrations:deleteConnectionDialog.secondaryButtonText',
        'Keep connection'
      )}
      isInProgress={statuses.delete.status === 'LOADING'}
      onSecondaryButtonClick={onCancel}
      dangerButtonText={t(
        i18nLocale,
        'integrations:deleteConnectionDialog.dangerButtonText',
        'Delete connection'
      )}
      onDangerButtonClick={onSubmit}
    >
      <ModalLayout>
        {statuses.delete.status === 'FAILURE' && statuses.delete.message && (
          <ErrorNotificationContainer>
            <ErrorNotification>{statuses.delete.message}</ErrorNotification>
          </ErrorNotificationContainer>
        )}
        <div>
          <Trans
            i18nKey="integrations:deleteConnectionDialog.warning"
            defaults="Some schedules and configurations might not work if the connection <bold>{{name}}</bold> is deleted."
            values={{ name: connection.name }}
            components={{ bold: <b>{''}</b> }}
          />
        </div>
        <Stack gap="medium">
          {Boolean(scheduleNames.length) && (
            <Stack gap="xsmall">
              <b>
                {t(
                  i18nLocale,
                  'integrations:deleteConnectionDialog.affectedSchedules',
                  'Affected schedules:'
                )}
              </b>
              <List>
                {scheduleNames.map((schedule, i) => (
                  <li key={i}>{schedule}</li>
                ))}
              </List>
            </Stack>
          )}
          {Boolean(configNames.length) && (
            <Stack gap="xsmall">
              <b>
                {t(
                  i18nLocale,
                  'integrations:deleteConnectionDialog.affectedConfigurations',
                  'Affected configurations:'
                )}
              </b>
              <List>
                {configNames.map((config, i) => (
                  <li key={i}>{config}</li>
                ))}
              </List>
            </Stack>
          )}
        </Stack>
        <div>
          {t(
            i18nLocale,
            'integrations:deleteConnectionDialog.unrecoverableNotice',
            'The connection can’t be recovered once deleted.'
          )}
        </div>
      </ModalLayout>
    </ModalTemplate>
  );
}

const List = styled.ul`
  padding-left: 30px;
  max-width: 400px;
`;

const ErrorNotificationContainer = styled.div`
  margin: ${s32} ${s32} 0 ${s32};
`;
